"use client";

import { differenceInMinutes, differenceInHours, differenceInDays, format } from "date-fns";
import { enUS } from "date-fns/locale";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Typography } from "@/components/shared/Typography";
import { ArticleListType } from "@/types/article";
import { getImageUrl } from "@/utils/getImageUrl";
import { getArticleAppLocale, getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { getLocalizedString } from "@/utils/getLocalizedString";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import CustomImage from "../shared/CustomImage";
import TitleTag from "../shared/TitleTag";

import { formatDate } from "@/utils/dateUtils";

interface VerticalArticleCardProps {
  variant?: "md" | "lg";
  article: ArticleListType;
  showSummary?: boolean;
  showDate?: boolean;
  showCategory?: boolean;
}

// const colors = ["#FF6E0D", "#007DFF", "#EB8D02", "#FF5575"];

export default function VerticalArticleCard({
  variant = "md",
  article,
  showSummary = false,
  showDate = false,
  showCategory = false,
}: VerticalArticleCardProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "en-IN";
  const language = getArticleAppLocale(article?.language, pathname);

  if (!article) return null;

  const {
    title,
    slug,
    image,
    summary,
    categoryId,
    subCategoryId,
    isLiveNews,
    publishedAt,
    isInPrint,
  } = article;

  const formatTimeAgo = () => {
    if (!publishedAt) return "";
    const now = new Date();
    const published = new Date(publishedAt);
    const diffMinutes = differenceInMinutes(now, published);
    const diffHours = differenceInHours(now, published);
    const diffDays = differenceInDays(now, published);

    if (language === "te") {
      if (diffMinutes < 60) {
        return `${diffMinutes} ${diffMinutes === 1 ? "నిమిషం" : "నిమిషాల"} క్రితం`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? "గంట" : "గంటల"} క్రితం`;
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? "రోజు" : "రోజుల"} క్రితం`;
      } else {
        return formatDate(published,language);
      }
    } else {
      if (diffMinutes < 60) {
        return `${diffMinutes} Min Ago`;
      } else if (diffHours < 24) {
        return `${diffHours} Hour${diffHours > 1 ? "s" : ""} Ago`;
      } else if (diffDays < 7) {
        return `${diffDays} Day${diffDays > 1 ? "s" : ""} Ago`;
      } else {
        return formatDate(published, language);
      }
    }
  };

  return (
    <div className="group">
      <Link
        href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
        aria-label={`View news: ${title}`}
      >
        <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden">
          <CustomImage
            src={getImageUrl(
              (variant === "lg" ? image?.sizes?.sd?.filename : image?.sizes?.ld?.filename) ||
                image?.filename,
            )}
            className={`w-full h-full object-cover object-${article.focusPosition} img-zoom-hover`}
            alt={image?.alt || ""}
            phImg={
              variant === "lg"
                ? "/images/placeholder-grey-sd.png"
                : "/images/placeholder-grey-ld.png"
            }
          />

          {showCategory && (
            <div className="absolute top-0 left-6 bg-[#007DFF] text-white text-xs px-2 uppercase py-[3px] font-[600]">
              {getLocalizedString(categoryId?.name, locale) || categoryId?.slug}
            </div>
          )}
        </div>
      </Link>
      <div className={variant === "md" ? "mt-3" : "mt-4"}>
        <Link
          href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
          aria-label={`View news: ${title}`}
        >
          <Typography
            language={language}
            variant={variant === "md" ? "h6" : "h5"}
            className="line-clamp-3"
          >
            {isLiveNews && <TitleTag labelKey="LIVE_NEWS" />}
            {isInPrint && <TitleTag labelKey="PRINT_EDITION" />}
            {title}
          </Typography>
        </Link>

        {showSummary && (
          <Link
            href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
            aria-live="polite"
            aria-label={`View news: ${title}`}
          >
            <Typography variant="body1" language={language} className="mt-3 line-clamp-4">
              {summary}
            </Typography>
          </Link>
        )}

        {showDate && (
          <span className="text-gray-500 dark:text-white mt-[4px] font-medium !text-[10px]">
            {formatTimeAgo()}
          </span>
        )}
      </div>
    </div>
  );
}
